import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCcw, Edit3, Trash2 } from 'lucide-react';
import type { NormalizedClinicalNote } from './types';

interface ClinicalNoteViewerProps {
  note: NormalizedClinicalNote;
  isRefreshing: boolean;
  isDeleting: boolean;
  onRefresh: () => void;
  onEdit: () => void;
  onDelete: () => void;
  formatDateTime: (value?: string | null) => string;
}

const sections = [
  { title: 'Temi', key: 'topics' as const },
  { title: 'Preoccupazioni', key: 'concerns' as const },
  { title: 'Obiettivi', key: 'goals' as const },
  { title: 'Progressi', key: 'progress' as const },
  { title: 'Sfide', key: 'challenges' as const },
  { title: 'Successi', key: 'successes' as const },
];

function ClinicalNoteViewer({
  note,
  isRefreshing,
  isDeleting,
  onRefresh,
  onEdit,
  onDelete,
  formatDateTime,
}: ClinicalNoteViewerProps) {
  const summaryUnavailable = !!note.review_failed_at;

  return (
    <section className="space-y-8">
      <header className="space-y-3 border-b border-border/50 pb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Nota del {formatDateTime(note.created_at)}
          </h2>
          <p className="text-sm text-muted-foreground">
            Ultimo aggiornamento {formatDateTime(note.updated_at)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={isRefreshing || summaryUnavailable}
          >
            {isRefreshing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rigenerazione…
              </>
            ) : (
              <>
                <RefreshCcw className="mr-2 h-4 w-4" /> Aggiorna sintesi
              </>
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit3 className="mr-2 h-4 w-4" /> Modifica
          </Button>
          <Button size="sm" variant="destructive" onClick={onDelete} disabled={isDeleting}>
            <Trash2 className="mr-2 h-4 w-4" /> Elimina
          </Button>
        </div>
      </header>

      <div className="max-h-[520px] overflow-y-auto pr-1">
        <div className="whitespace-pre-wrap rounded-lg bg-muted/20 p-4 text-base leading-relaxed text-foreground">
          {note.body}
        </div>
      </div>

      <section className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">Riassunto strutturato</h3>
          <p className="text-sm text-muted-foreground">Generato automaticamente dal modello.</p>
        </div>
        {summaryUnavailable ? (
          <div className="rounded-lg border border-dashed border-muted-foreground/40 p-4 text-sm text-muted-foreground">
            {note.summary_unavailable_message || 'Riassunto non disponibile.'}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Sintesi narrativa
              </p>
              <p className="whitespace-pre-wrap rounded-lg bg-muted/20 p-4 text-sm leading-relaxed text-muted-foreground">
                {note.summary.description || 'Nessuna descrizione disponibile.'}
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {sections.map(({ title, key }) => (
                <SummaryList key={key} title={title} items={note.summary[key]} />
              ))}
            </div>
          </div>
        )}
      </section>
    </section>
  );
}

function SummaryList({ title, items }: { title: string; items: string[] }) {
  const hasItems = items.length > 0;
  return (
    <div className="space-y-2 rounded-lg bg-muted/10 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {hasItems ? (
        <ul className="space-y-2 text-sm leading-relaxed text-foreground">
          {items.map((item, index) => (
            <li key={`${title}-${index}`} className="rounded-md bg-background/70 px-2 py-1">
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Nessun elemento rilevato.</p>
      )}
    </div>
  );
}

export default memo(ClinicalNoteViewer);
